/**
 * SimPanel — Single agent column inside MultiSimulator.
 * Supabase-native: creates synthetic contacts, runs agent.execute() with
 * integration payload so agentfactory saves messages + runs eval pipeline.
 * Messages are displayed via useMessages() which polls Supabase.
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import Markdown from "react-markdown"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { Loader2, X, Bot, User, RefreshCw, MessageSquare, AlertTriangle, ChevronLeft, ChevronRight, FlaskConical } from "lucide-react"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { useSupabaseSimulation, useEvalItemMap, useAgentFlags } from "../hooks"
import { QuickReplies } from "./QuickReplies"
import { extractQuickReplies } from "../utils"
import { MessageBubble } from "@/pages/dev/moderation/components/MessageBubble"
import { FormPanel } from "@/pages/dev/moderation/components"
import { useMessages, useFlags, useRatings, useComments } from "@/pages/dev/moderation/hooks"
import type { FlagType, DetectedItem, ModerationMessage } from "@/pages/dev/moderation/types"
import { messageHasActiveModerationFlags, isBotMessage, expandBreaks } from "@/pages/dev/moderation/utils/conversationUtils"
import type { MessageComment } from "@/pages/dev/moderation/hooks/useComments"
import { supabase } from "@/lib/data"
import { ulid } from "ulid"

interface SimPanelProps {
  agentId: string
  agentTitle: string
  /** A stable label for the simulated user (displayed + used as synthetic contact name) */
  label?: string
  teamId: string
  onRemove: () => void
  onSendingChange?: (isSending: boolean) => void
}

export interface SimPanelHandle {
  sendMessage: (message: string) => Promise<void>
  isSending: boolean
}

export const SimPanel = forwardRef<SimPanelHandle, SimPanelProps>(function SimPanel(
  { agentId, agentTitle, label, teamId, onRemove, onSendingChange },
  ref
) {
  const navigate = useNavigate()
  const [contactId, setContactId] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isEvaluating, setIsEvaluating] = useState(false)
  const [statusText, setStatusText] = useState<string | null>(null)
  const [isEscalated, setIsEscalated] = useState(false)
  const [comments, setComments] = useState<MessageComment[]>([])
  const [flaggedIndex, setFlaggedIndex] = useState(0)
  // Local message cache — seeded with DB data from useMessages but never cleared
  // between sends. This is the source of truth for display; useMessages data is
  // merged in so evals/flags appear once they land.
  const [localMessages, setLocalMessages] = useState<ModerationMessage[]>([])
  const pendingContactIdRef = useRef<string | null>(null)

  // Notify parent whenever sending state changes so it can update the send button
  const onSendingChangeRef = useRef(onSendingChange)
  onSendingChangeRef.current = onSendingChange
  useEffect(() => { onSendingChangeRef.current?.(isSending) }, [isSending])

  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()

  const { sendSimulatedMessage, ensureContact } = useSupabaseSimulation()
  const evalItemMap = useEvalItemMap(teamId)
  const agentFlags = useAgentFlags(agentId)
  const { messages, loading: loadingMessages, refresh: refreshMessages, contact: liveContact } = useMessages(contactId)
  const { toggleFlag, resolveAllFlags } = useFlags()
  const { submitRating } = useRatings()
  const { getComments, addComment, deleteComment, getCommentsForMessage } = useComments(contactId || "")

  // Sync escalation from polled contact data
  useEffect(() => {
    if (typeof liveContact?.hitl === "boolean") {
      setIsEscalated(liveContact.hitl)
      return
    }
    if (liveContact?.moderation_data) {
      try {
        const d = JSON.parse(liveContact.moderation_data as string)
        setIsEscalated(d?.handled_by === "human")
      } catch {}
    }
  }, [liveContact?.hitl, liveContact?.moderation_data])

  // Load comments when contactId changes
  useEffect(() => {
    if (!contactId) { setComments([]); return }
    getComments().then(setComments).catch(() => setComments([]))
  }, [contactId, getComments])

  // Merge DB messages into local cache — never replace, only add/update.
  // Fingerprint includes eval fields so flag/item updates after evals complete
  // correctly replace the stale local entries.
  const lastMergedFingerprintRef = useRef("")
  useEffect(() => {
    if (messages.length === 0) return
    const fp = messages.map(m =>
      `${m.id}:${JSON.stringify(m.custom_message_flags || [])}:${JSON.stringify(m.user_detected_items || [])}:${JSON.stringify(m.agent_detected_items || [])}:${m.highrisk ?? ""}:${m.lowconf ?? ""}:${m.askhuman ?? ""}:${m.rating ?? ""}:${m.agent_concern_level ?? ""}:${m.agent_appropriate ?? ""}`
    ).join("|")
    if (fp === lastMergedFingerprintRef.current) return
    lastMergedFingerprintRef.current = fp
    setLocalMessages(messages)
  }, [messages])

  // displayMessages: always show localMessages (which includes optimistic entries
  // until real DB data overwrites them by id). Never blank.
  const displayMessages = useMemo(() => expandBreaks(localMessages), [localMessages])

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector("[data-slot=scroll-area-viewport]") as HTMLElement
      const target = viewport || scrollRef.current
      target.scrollTop = target.scrollHeight
    }
  }, [displayMessages, isSending])

  // Flagged messages navigator
  const flaggedMessages = useMemo(
    () => displayMessages.filter(m => messageHasActiveModerationFlags(m, evalItemMap)),
    [displayMessages, evalItemMap]
  )

  useEffect(() => {
    setFlaggedIndex(i => Math.min(i, Math.max(0, flaggedMessages.length - 1)))
  }, [flaggedMessages.length])

  const scrollToFlagged = useCallback((idx: number) => {
    const msg = flaggedMessages[idx]
    if (!msg) return
    messageRefs.current[msg.id]?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [flaggedMessages])

  // Quick replies parsed from the latest bot message's [bracketed] options —
  // same mechanism as the agent flow chat panel.
  const quickReplies = useMemo(() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const msg = displayMessages[i]
      if (isBotMessage(msg)) return extractQuickReplies(msg.message)
      if (msg.role === "user") break
    }
    return []
  }, [displayMessages])

  const sendMessage = async (messageToSend: string) => {
    if (!messageToSend || !teamId || isSending) return
    setIsSending(true)

    // Append user message to local display immediately — no waiting for DB
    const optimisticId = `optimistic-${ulid()}`
    const optimisticUserMsg: ModerationMessage = {
      id: optimisticId,
      contact: contactId,
      role: "user",
      message: messageToSend,
      channel: "app",
      created_at: new Date().toISOString(),
      user_detected_items: null,
      agent_detected_items: null,
      highrisk: null,
      lowconf: null,
      askhuman: null,
      rating: null,
    }
    setLocalMessages(prev => [...prev, optimisticUserMsg])

    // ── Human mode — save the user message only, don't run the agent ──
    if (isEscalated) {
      try {
        const resolvedId = await ensureContact({ contactId, teamId, label })
        if (resolvedId !== contactId) setContactId(resolvedId)

        const agentIdNum = agentId ? (parseInt(agentId, 10) || null) : null
        const { error } = await supabase
          .from("messages")
          .insert({
            contact: resolvedId,
            team: teamId,
            role: "user",
            message: messageToSend,
            channel: "app",
            agent: agentIdNum,
          })
        if (error) throw new Error(error.message)
        refreshMessages()
      } catch (err) {
        // Remove the optimistic entry on failure
        setLocalMessages(prev => prev.filter(m => m.id !== optimisticId))
        toast({
          title: `${agentTitle}: Error`,
          description: err instanceof Error ? err.message : "Failed to save message",
          variant: "destructive",
        })
      } finally {
        setIsSending(false)
      }
      return
    }

    setStatusText("Running agent…")

    try {
      const result = await sendSimulatedMessage({
        contactId,
        teamId,
        agentId,
        message: messageToSend,
        label,
        onMessageInserted: (newId) => {
          // Stash the resolved contactId — don't apply to state yet so
          // useMessages doesn't re-subscribe mid-execution.
          pendingContactIdRef.current = newId
        },
      })

      if (!result.success) {
        throw new Error(result.error || "Agent execution failed")
      }

      // Agent finished — both messages are now in DB. Apply contactId to
      // trigger a fetch, or refresh if contactId didn't change.
      const resolvedId = result.newContactId || pendingContactIdRef.current
      pendingContactIdRef.current = null

      if (resolvedId && resolvedId !== contactId) {
        setContactId(resolvedId)
      } else {
        refreshMessages()
      }

      // Track async flag + eval pipeline so we can show the indicator
      if (result.evalPromise) {
        setIsEvaluating(true)
        result.evalPromise
          .then(() => refreshMessages())
          .finally(() => setIsEvaluating(false))
      }
    } catch (err) {
      pendingContactIdRef.current = null
      // Remove the optimistic user message on failure
      setLocalMessages(prev => prev.filter(m => m.id !== optimisticId))
      toast({
        title: `${agentTitle}: Error`,
        description: err instanceof Error ? err.message : "Failed to send message",
        variant: "destructive",
      })
    } finally {
      setIsSending(false)
      setStatusText(null)
    }
  }

  useImperativeHandle(ref, () => ({ sendMessage, isSending }), [sendMessage, isSending])

  const handleToggleEscalation = async () => {
    if (!contactId) return
    const newMode = isEscalated ? "bot" : "human"
    try {
      const current = liveContact?.moderation_data ? (() => { try { return JSON.parse(liveContact.moderation_data as string) } catch { return {} } })() : {}
      await supabase
        .from("contacts")
        .update({
          moderation_data: JSON.stringify({ ...current, handled_by: newMode }),
          hitl: newMode === "human",
        } as any)
        .eq("id", contactId)
      setIsEscalated(!isEscalated)
    } catch {
      /* toggle failed silently */
    }
  }

  const handleToggleFlag = async (messageId: string, flagType: FlagType, currentItems: DetectedItem[], currentFlags?: FlagDetectedItem[]) => {
    try {
      await toggleFlag(messageId, flagType, currentItems, currentFlags)
      await refreshMessages()
    } catch {
      /* toggle failed silently */
    }
  }

  const handleResolveAll = async (messageId: string, currentItems?: DetectedItem[], currentFlags?: FlagDetectedItem[]) => {
    const msg = displayMessages.find(m => m.id === messageId)
    if (!msg) return
    try {
      await resolveAllFlags(messageId, currentItems || msg.user_detected_items || [], currentFlags || msg.custom_message_flags || [])
      await refreshMessages()
    } catch {
      /* resolve failed silently */
    }
  }

  const handleAddComment = useCallback(async (messageId: string, text: string) => {
    if (!contactId) return
    const updated = await addComment(text, "simulator", "Simulator", messageId)
    setComments(updated)
  }, [contactId, addComment])

  const handleDeleteComment = useCallback(async (commentId: string) => {
    if (!contactId) return
    const updated = await deleteComment(commentId)
    setComments(updated)
  }, [contactId, deleteComment])

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden bg-background">
      {/* Panel header */}
      <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        <button
          type="button"
          onClick={() => agentId && navigate(`/agent/${agentId}`)}
          disabled={!agentId}
          title={agentId ? `Open ${agentTitle} configuration` : agentTitle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left rounded hover:text-primary disabled:pointer-events-none group/agent"
        >
          <Bot className="h-3.5 w-3.5 text-muted-foreground shrink-0 group-hover/agent:text-primary" />
          <span className="text-xs font-semibold truncate leading-none group-hover/agent:underline">
            <Markdown
              components={{
                // Render everything inline — no block wrappers
                p: ({ children }) => <>{children}</>,
                strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                code: ({ children }) => <code className="bg-muted px-0.5 rounded text-[10px]">{children}</code>,
              }}
              allowedElements={["p", "strong", "em", "code"]}
              unwrapDisallowed
            >{agentTitle}</Markdown>
          </span>
        </button>

        {isSending && !isEscalated && (
          <Badge variant="secondary" className="text-[10px] h-4 px-1.5 gap-1 shrink-0">
            <Loader2 className="h-2.5 w-2.5 animate-spin" />
            {statusText || "Thinking…"}
          </Badge>
        )}
        {!isSending && isEvaluating && (
          <Badge className="text-[10px] h-4 px-1.5 gap-1 shrink-0 bg-violet-100 text-violet-700 border border-violet-200 hover:bg-violet-100">
            <FlaskConical className="h-2.5 w-2.5 animate-pulse" />
            Evaluating…
          </Badge>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          title="Remove this panel"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>

      {/* Sub-header: label + refresh + flagged navigator + escalation toggle */}
      <div className="shrink-0 px-3 py-1 border-b bg-muted/10 flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground flex-1 truncate">{label || "Simulated user"}</span>

        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 text-muted-foreground"
          onClick={() => refreshMessages()}
          title="Refresh messages"
        >
          <RefreshCw className="h-2.5 w-2.5" />
        </Button>

        {flaggedMessages.length > 0 && (
          <div className="flex items-center gap-0.5 border rounded px-1.5 py-0.5 bg-amber-50 dark:bg-amber-950/20 border-amber-300">
            <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
            <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium mx-0.5">
              {flaggedIndex + 1}/{flaggedMessages.length}
            </span>
            <Button
              variant="ghost" size="icon" className="h-3.5 w-3.5"
              onClick={() => { const i = Math.max(0, flaggedIndex - 1); setFlaggedIndex(i); scrollToFlagged(i) }}
              disabled={flaggedIndex === 0}
            ><ChevronLeft className="h-2.5 w-2.5" /></Button>
            <Button
              variant="ghost" size="icon" className="h-3.5 w-3.5"
              onClick={() => { const i = Math.min(flaggedMessages.length - 1, flaggedIndex + 1); setFlaggedIndex(i); scrollToFlagged(i) }}
              disabled={flaggedIndex === flaggedMessages.length - 1}
            ><ChevronRight className="h-2.5 w-2.5" /></Button>
          </div>
        )}

        {contactId && (
          <button
            onClick={handleToggleEscalation}
            title={isEscalated ? "Switch to bot" : "Switch to human mode"}
            className={cn(
              "flex items-center gap-1 border rounded px-1.5 py-0.5 shrink-0 text-[10px] font-medium transition-colors",
              isEscalated
                ? "border-red-400 bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            )}
          >
            {isEscalated ? <User className="h-2.5 w-2.5" /> : <Bot className="h-2.5 w-2.5" />}
            {isEscalated ? "Human" : "Bot"}
          </button>
        )}
      </div>

      {/* Body: messages left, Magic Form panel right */}
      <div className="flex flex-1 min-h-0">
      <ScrollArea className="flex-1 min-w-0 px-3 py-2" ref={scrollRef}>
        {!contactId && !loadingMessages && displayMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-24 text-muted-foreground">
            <MessageSquare className="h-6 w-6 mb-1.5 opacity-30" />
            <p className="text-xs">Waiting for first message…</p>
          </div>
        ) : loadingMessages && displayMessages.length === 0 ? (
          <div className="space-y-3">
            {[1, 2].map(i => <Skeleton key={i} className="h-12 w-4/5" />)}
          </div>
        ) : (
          <div className="pt-1">
            {displayMessages.map((msg) => (
              <div
                key={msg.id}
                ref={(el) => { messageRefs.current[msg.id] = el }}
                className={cn(msg.id.startsWith("optimistic-") && "opacity-60")}
              >
                <MessageBubble
                  message={msg}
                  evalItems={evalItemMap}
                  agentFlags={agentFlags}
                  onToggleFlag={(flagType, currentItems, currentFlags) => handleToggleFlag(msg.id, flagType, currentItems, currentFlags)}
                  onResolveAll={(currentItems, currentFlags) => handleResolveAll(msg.id, currentItems, currentFlags)}
                  onRate={(rating, example) => submitRating(msg.id, rating, example)}
                  comments={getCommentsForMessage(comments, msg.id)}
                  onAddComment={(text) => handleAddComment(msg.id, text)}
                  onDeleteComment={handleDeleteComment}
                />
              </div>
            ))}

            {isSending && !isEscalated && (
              <div className="flex justify-start mb-2">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {statusText || "Running agent…"}
                  </div>
                </div>
              </div>
            )}


            {!isSending && isEvaluating && (
              <div className="flex justify-end mb-1 mr-1">
                <div className="flex items-center gap-1.5 text-[10px] text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
                  <FlaskConical className="h-2.5 w-2.5 animate-pulse shrink-0" />
                  Running evaluations…
                </div>
              </div>
            )}

            {isEscalated && contactId && (
              <div className="flex justify-center my-2">
                <span className="text-[10px] text-muted-foreground border border-dashed rounded px-2 py-1">
                  Human mode — waiting for operator response in moderation panel
                </span>
              </div>
            )}

            {!isSending && !isEscalated && (
              <QuickReplies
                replies={quickReplies}
                onSelect={(reply) => sendMessage(reply)}
                disabled={isSending}
              />
            )}
          </div>
        )}
      </ScrollArea>

      {/* Magic Form panel */}
      {contactId && (
        <div className="w-[240px] shrink-0 border-l flex flex-col min-h-0">
          <FormPanel
            variant="embedded"
            contactId={contactId}
            teamId={teamId}
            agentId={agentId}
            pollMs={6000}
            refreshSignal={`${displayMessages.length}:${isSending ? 1 : 0}:${isEvaluating ? 1 : 0}`}
          />
        </div>
      )}
      </div>
    </div>
  )
})
